import {
    mxConstants,
    mxEdgeHandler,
    mxEventObject,
    mxEventSource,
    mxMouseEvent,
    mxResources,
    mxUtils
} from './mxGraphFactory';
import {createLogger} from '../Common/logger';

const LOGGER = createLogger("PopupMenu");

export const PopupMenu = function (editorUi) {
    this.editorUi = editorUi;
    this.menus = {};
};

/**
 * Adds the label menu items to the given menu and parent.
 */
PopupMenu.prototype.put = function (name, menu) {
    this.menus[name] = menu;
    return menu;
};

/**
 * Adds the label menu items to the given menu and parent.
 */
PopupMenu.prototype.get = function (name) {
    return this.menus[name];
};

/**
 * Adds the given submenu.
 */
PopupMenu.prototype.addSubmenu = function (name, menu, parent, label) {
    const entry = this.get(name);

    if (menu != null && entry.isEnabled()) {
        const submenu = menu.addItem(label || mxResources.get(name), null, null, parent, null, true);
        this.addMenu(name, menu, submenu);
    }
};

/**
 * Adds the label menu items to the given menu and parent.
 */
PopupMenu.prototype.addMenu = function (name, popupMenu, parent) {
    const menu = this.get(name);

    if (menu != null && menu.isEnabled()) {
        this.get(name).execute(popupMenu, parent);
    }
};

/**
 * Creates the keyboard event handler for the current graph and history.
 */
PopupMenu.prototype.addMenuItem = function (menu, key, parent, trigger, sprite, label) {
    const action = this.editorUi.actions.get(key);

    LOGGER.debug('addMenuItem(..)', action);

    if (action != null && action.isEnabled() && action.visible) {
        const item = menu.addItem(label || action.label, null, () => action.funct(trigger), parent, sprite, action.isEnabled());

        this.addShortcut(item, action);

        return item;
    }

    return null;
};

/**
 * Adds a checkmark to the given menuitem.
 */
PopupMenu.prototype.addShortcut = function (item, action) {
    if (action.shortcut != null) {
        const td = item.firstChild.nextSibling.nextSibling;
        const span = document.createElement('span');
        span.style.color = 'gray';
        mxUtils.write(span, action.shortcut);
        td.appendChild(span);
    }
};

/**
 * Creates the keyboard event handler for the current graph and history.
 */
PopupMenu.prototype.addMenuItems = function (menu, keys, parent, trigger, sprites) {
    for (let i = 0; i < keys.length; i++) {
        if (keys[i] === '-') {
            menu.addSeparator(parent);
        } else {
            this.addMenuItem(menu, keys[i], parent, trigger, (sprites != null) ? sprites[i] : null);
        }
    }
};

/**
 * Creates the keyboard event handler for the current graph and history.
 */
PopupMenu.prototype.createPopupMenu = function (menu, cell, evt) {
    const graph = this.editorUi.editor.graph;
    const state = graph.view.getState(cell);
    menu.smartSeparators = true;

    if (graph.isSelectionEmpty()) {
        this.addMenuItems(menu, ['undo', 'redo', 'pasteHere'], null, evt);
    } else {
        const menus = [];
        if (graph.isCellDeletable(graph.getSelectionCell())) {
            menus.push('delete', '-');
        }

        if (graph.getSelectionCell() &&
            graph.getSelectionCell().vertex &&
            !graph.getSelectionCell().isStart() &&
            !graph.getSelectionCell().isMap()) {
            menus.push('cut', 'copy', '-', 'duplicate');
        }

        this.addMenuItems(menu, menus, null, evt);
    }

    if (state != null) {
        let hasWaypoints = false;

        if (graph.getModel().isEdge(cell) && mxUtils.getValue(state.style, mxConstants.STYLE_EDGE, null) !== 'entityRelationEdgeStyle' &&
            mxUtils.getValue(state.style, mxConstants.STYLE_SHAPE, null) !== 'arrow') {
            let handler = graph.selectionCellsHandler.getHandler(cell);
            let isWaypoint = false;

            if (handler instanceof mxEdgeHandler && handler.bends != null && handler.bends.length > 2) {
                const index = handler.getHandleForEvent(graph.updateMouseEvent(new mxMouseEvent(evt)));

                const rmWaypointAction = this.editorUi.actions.get('removeWaypoint');
                rmWaypointAction.handler = handler;
                rmWaypointAction.index = index;

                isWaypoint = index > 0 && index < handler.bends.length - 1;
            }

            menu.addSeparator();
            this.addMenuItems(menu, [(isWaypoint) ? 'removeWaypoint' : 'addWaypoint'], null, evt);

            const geo = graph.getModel().getGeometry(cell);
            hasWaypoints = geo != null && geo.points != null && geo.points.length > 0;
        }

        if (graph.getSelectionCount() === 1 && (hasWaypoints || (graph.getModel().isVertex(cell) &&
            graph.getModel().getEdgeCount(cell) > 0))) {
            this.addMenuItems(menu, ['-', 'clearWaypoints'], null, evt);
        }
    }

    if (graph.isSelectionEmpty()) {
        this.addMenuItems(menu, ['-', 'selectVertices', 'selectEdges', 'selectAll'], null, evt);
    }
};

/**
 * Constructs a new menubar for the given editor.
 */
const Menubar = function (editorUi, container) {
    this.editorUi = editorUi;
    this.container = container;
};

/**
 * Adds the menubar elements.
 */
Menubar.prototype.hideMenu = function () {
    this.editorUi.hideCurrentMenu();
};

/**
 * Creates the keyboard event handler for the current graph and history.
 */
Menubar.prototype.destroy = function () {
    // do nothing
};

/**
 * Constructs a new action for the given parameters.
 */
function Menu(funct, enabled) {
    mxEventSource.call(this);
    this.funct = funct;
    this.enabled = (enabled != null) ? enabled : true;
}

// Menu inherits from mxEventSource
mxUtils.extend(Menu, mxEventSource);

/**
 * Sets the enabled state of the action and fires a stateChanged event.
 */
Menu.prototype.setEnabled = function (value) {
    if (this.enabled !== value) {
        this.enabled = value;
        this.fireEvent(new mxEventObject('stateChanged'));
    }
};
